import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalRef } from '@/components/@IntranetLibrary/Modal';
import { useSnackbar } from '@/hooks/global-tools';
import CheckIcon from '@/public/icons/approval/check.svg';
import BanIcon from '@/public/icons/approval/ban.svg';
import PauseIcon from '@/public/icons/approval/pause.svg';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import type { DocumentStatus } from '../types';
import OpinionEditor from './OpinionEditor';
import { DecisionButton, OpinionFooter, OpinionNotice } from './styles';

/** 의견입력 결재/반려/보류 모달 — 노출/권한 판정은 부모(DocumentActions) 소관. */

type OpinionDecisionModalProps = {
  open: boolean;
  status: DocumentStatus; // 결재 상태에 따른 버튼 노출 제어용. (완료 C는 액션 불필요)
  onClose: () => void; // 닫기(X)/백드롭 클릭 시 호출. 부모가 open=false 로 내려서 모달 닫힘. 입력값 초기화는 본 모달이 담당.
  onDecide: (action: ApprovalDecisionAction, opinion: string) => void; // 결재/반려/보류 버튼 클릭 시 부모에게 액션 + 의견 전달.
  submitting?: boolean; // 부모의 결재 API 호출 진행 중 여부. true 면 버튼 비활성(중복 클릭 방지)
};

const OpinionDecisionModal = ({
  open,
  status,
  onClose,
  onDecide,
  submitting = false,
}: OpinionDecisionModalProps) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const modalRef = useRef<ModalRef>(null);
  const [opinionHtml, setOpinionHtml] = useState('');
  const [opinionLength, setOpinionLength] = useState(0);

  // 컨트롤드 open → imperative Modal 동기화. 재오픈 시 입력 state 초기화
  useEffect(() => {
    if (open) {
      setOpinionHtml('');
      setOpinionLength(0);
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [open]);

  const handleClose = () => {
    setOpinionHtml('');
    setOpinionLength(0);
    onClose();
  };

  const handleOpinionChange = (html: string, length: number) => {
    setOpinionHtml(html);
    setOpinionLength(length);
  };

  // 레거시 apprModal 푸터 노출 조건. 임시저장(S)은 상신이라 결재만 — 반려·보류 대상이 없음
  const showConfirm = status === 'P' || status === 'H' || status === 'S';
  const showReject = status === 'P' || status === 'H';
  const showHold = status === 'P';

  const decide = (action: ApprovalDecisionAction) => {
    if (submitting) return;
    // 반려는 사유 의견 필수(레거시).
    if (action === 'reject' && opinionLength === 0) {
      showError({ message: t('반려사유 의견을 입력해 주세요.') });
      return;
    }
    // 빈 의견은 ''로 정규화 — 에디터 빈 마크업(<p><br></p>)이 그대로 저장돼 OpinionSlot 빈 박스로 뜨는 것 방지.
    onDecide(action, opinionLength === 0 ? '' : opinionHtml);
  };

  return (
    <Modal
      ref={modalRef}
      title={t('의견입력 결재, 반려')}
      size="small"
      handleClose={handleClose}
      isCloseButtonDisabled={submitting}
      footerContent={
        <OpinionFooter>
          {showConfirm && (
            <DecisionButton
              type="button"
              tone="success"
              disabled={submitting}
              onClick={() => decide('confirm')}
            >
              <CheckIcon />
              <span className="btn__label">{t('결재')}</span>
            </DecisionButton>
          )}
          {showReject && (
            <DecisionButton
              type="button"
              tone="rejected"
              disabled={submitting}
              onClick={() => decide('reject')}
            >
              <BanIcon />
              <span className="btn__label">{t('반려')}</span>
            </DecisionButton>
          )}
          {showHold && (
            <DecisionButton
              type="button"
              tone="hold"
              disabled={submitting}
              onClick={() => decide('hold')}
            >
              <PauseIcon />
              <span className="btn__label">{t('보류')}</span>
            </DecisionButton>
          )}
        </OpinionFooter>
      }
    >
      <>
        <OpinionNotice>
          {t('의견 입력 후, 결재, 반려 혹은 보류 버튼을 누르세요')}
        </OpinionNotice>
        <OpinionEditor onChange={handleOpinionChange} length={opinionLength} />
      </>
    </Modal>
  );
};

export default OpinionDecisionModal;
